import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { closeAssistant } from "portal/slices/assistantSlice";
import { useBranding } from "context/BrandingProvider";
import { getCurrencySymbol } from "portal/utils/formatters";

// ── Category badge config ───────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  finance: { label: "Finance", bg: "bg-success-50", text: "text-success-600", dot: "bg-success-500" },
  patient: { label: "Patient", bg: "bg-info-50", text: "text-info-600", dot: "bg-info-500" },
  appointment: { label: "Appointment", bg: "bg-brand-50", text: "text-brand-600", dot: "bg-brand-500" },
  analytics: { label: "Analytics", bg: "bg-warning-50", text: "text-warning-600", dot: "bg-warning-500" },
  general: { label: "General", bg: "bg-surface-bg", text: "text-content-secondary", dot: "bg-content-tertiary" },
};

const STATUS_COLORS = {
  positive: "text-success-600",
  negative: "text-danger-500",
  neutral: "text-content-secondary",
};

// ── Copy button ─────────────────────────────────────────────────────────────

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="w-6 h-6 flex items-center justify-center rounded-button hover:bg-surface-hover transition-colors flex-shrink-0"
      title={copied ? "Copied!" : "Copy response"}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="stroke-success-600" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="stroke-content-tertiary" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
      )}
    </button>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const ConfidenceBadge = ({ confidence }) => {
  if (!confidence && confidence !== 0) return null;
  const color = confidence >= 70 ? "text-success-600 bg-success-50" : confidence >= 40 ? "text-warning-600 bg-warning-50" : "text-danger-500 bg-danger-50";
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${color}`}>
      {confidence}% match
    </span>
  );
};

const CategoryBadge = ({ category }) => {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.general;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const DataTable = ({ items, columns }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-2 border border-surface-border rounded-card overflow-hidden">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-surface-hover">
            {columns.map((col, i) => (
              <th key={i} className="px-2.5 py-1.5 text-left font-medium text-content-secondary">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="border-t border-surface-border">
              {columns.map((col, j) => (
                <td key={j} className="px-2.5 py-1.5 text-content-primary">
                  {col.format ? col.format(item[col.key]) : item[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReportSection = ({ section }) => (
  <div className="mb-3">
    <h4 className="text-xs font-semibold text-content-primary mb-1.5">{section.title}</h4>
    <div className="space-y-1">
      {section.items.map((item, i) => (
        <div key={i} className="flex items-center justify-between text-xs">
          <span className="text-content-secondary">{item.label}</span>
          <span className={`font-medium ${STATUS_COLORS[item.status] || "text-content-primary"}`}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const RecommendationCard = ({ rec }) => {
  const icons = {
    warning: { bg: "bg-warning-50", border: "border-warning-100", text: "text-warning-600", icon: "!" },
    critical: { bg: "bg-danger-50", border: "border-danger-100", text: "text-danger-600", icon: "!!" },
    success: { bg: "bg-success-50", border: "border-success-100", text: "text-success-600", icon: "✓" },
    info: { bg: "bg-info-50", border: "border-info-100", text: "text-info-600", icon: "i" },
  };
  const style = icons[rec.type] || icons.info;

  return (
    <div className={`flex items-start gap-2 px-2.5 py-2 rounded-button border ${style.bg} ${style.border}`}>
      <span className={`w-4 h-4 flex items-center justify-center rounded-badge text-[10px] font-bold flex-shrink-0 mt-0.5 ${style.text} bg-surface-card`}>
        {style.icon}
      </span>
      <p className={`text-[11px] leading-relaxed ${style.text}`}>{rec.message}</p>
    </div>
  );
};

const SuggestionChips = ({ suggestions, onSuggestionClick }) => {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-[10px] text-content-tertiary mb-1.5">Try these:</p>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => onSuggestionClick(s.query)}
            className="px-2.5 py-1 text-[11px] font-medium rounded-badge border border-surface-border text-content-secondary hover:border-brand-500 hover:text-brand-500 hover:bg-brand-500/5 transition-all"
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const CapabilityList = ({ capabilities }) => {
  if (!capabilities || capabilities.length === 0) return null;
  return (
    <div className="mt-2 space-y-1">
      {capabilities.map((cap, i) => (
        <div key={i} className="flex items-start gap-2 text-xs">
          <span className="text-brand-500 font-mono text-[11px] flex-shrink-0 mt-px">{cap.command}</span>
          <span className="text-content-tertiary">—</span>
          <span className="text-content-secondary">{cap.description}</span>
        </div>
      ))}
    </div>
  );
};

// ── Build copyable text from response ───────────────────────────────────────

const buildCopyText = (response) => {
  const parts = [];
  if (response.title) parts.push(response.title);
  if (response.message) parts.push(response.message);

  const data = response.data;
  if (!data) return parts.join("\n");

  // Report text summary
  if (data.textSummary) return data.textSummary;

  // Table data. Only arrays are tables: a trend answer's `data.patients` is the patient
  // growth trend object, and calling forEach on it took the whole page down — on every
  // reload too, since the conversation is kept in sessionStorage.
  if (Array.isArray(data.invoices)) {
    data.invoices.forEach(inv => {
      parts.push(`#${inv.id} | ${inv.category} | ${getCurrencySymbol()}${inv.amount} | Pending: ${getCurrencySymbol()}${inv.remaining}`);
    });
  }
  if (Array.isArray(data.patients)) {
    data.patients.forEach(p => {
      parts.push(`${p.name} | ${p.status} | ${p.phone || ''}`);
    });
  }
  if (Array.isArray(data.topPerformers)) {
    data.topPerformers.forEach(d => {
      parts.push(`${d.name} — ${d.sessions} sessions`);
    });
  }
  if (Array.isArray(data.capabilities)) {
    data.capabilities.forEach(c => {
      parts.push(`${c.command} — ${c.description}`);
    });
  }

  return parts.join("\n");
};

// ── Main renderer ───────────────────────────────────────────────────────────

const AssistantResponseRenderer = ({ response, onSuggestionClick }) => {
  const { currency } = useBranding();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  if (!response) return null;

  const { type, category, title, message, data, route, actionLabel, confidence, suggestions } = response;

  const handleNavigate = () => {
    if (route) {
      dispatch(closeAssistant());
      navigate(route);
    }
  };

  // Simple responses (greeting, thanks) — no card wrapper
  if (type === "greeting" || type === "thanks") {
    return (
      <div className="bg-surface-card rounded-card border border-surface-border shadow-elevation-1 overflow-hidden">
        <div className="px-3.5 py-2.5">
          <div className="flex items-start justify-between">
            <p className="text-xs text-content-secondary leading-relaxed flex-1">{message}</p>
            <CopyButton text={message} />
          </div>
          {suggestions && (
            <SuggestionChips suggestions={suggestions} onSuggestionClick={onSuggestionClick} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-card rounded-card border border-surface-border shadow-elevation-1 overflow-hidden">
      {/* Header */}
      <div className="px-3.5 py-2.5 border-b border-surface-border">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <CategoryBadge category={category} />
            <ConfidenceBadge confidence={confidence} />
          </div>
          <CopyButton text={buildCopyText(response)} />
        </div>
        {title && <h3 className="text-sm font-semibold text-content-primary leading-snug">{title}</h3>}
      </div>

      {/* Body */}
      <div className="px-3.5 py-2.5">
        {/* Message */}
        <p className="text-xs text-content-secondary leading-relaxed">{message}</p>

        {/* Help — capability list */}
        {type === "help" && data?.capabilities && (
          <CapabilityList capabilities={data.capabilities} />
        )}

        {/* Data summary — invoice list */}
        {type === "data_summary" && data?.invoices && (
          <DataTable
            items={data.invoices}
            columns={[
              { key: "id", label: "ID" },
              { key: "category", label: "Type" },
              { key: "amount", label: "Amount", format: (v) => `${currency}${v?.toLocaleString("en-IN")}` },
              { key: "remaining", label: "Pending", format: (v) => `${currency}${v?.toLocaleString("en-IN")}` },
            ]}
          />
        )}

        {/* Data summary — patient list */}
        {type === "data_summary" && data?.patients && (
          <DataTable
            items={data.patients}
            columns={[
              { key: "name", label: "Name" },
              { key: "status", label: "Status" },
              { key: "phone", label: "Phone" },
            ]}
          />
        )}

        {/* Data summary — top performers */}
        {type === "data_summary" && data?.topPerformers && (
          <DataTable
            items={data.topPerformers}
            columns={[
              { key: "name", label: "Therapist" },
              { key: "sessions", label: "Sessions" },
            ]}
          />
        )}

        {/* Data summary — therapy breakdown */}
        {type === "data_summary" && data?.therapies && (
          <DataTable
            items={data.therapies}
            columns={[
              { key: "name", label: "Therapy" },
              { key: "count", label: "Invoices" },
              { key: "revenue", label: "Revenue", format: (v) => `${currency}${v?.toLocaleString("en-IN")}` },
            ]}
          />
        )}

        {/* Data summary — therapy breakdown from appointments */}
        {type === "data_summary" && data?.therapyBreakdown && !data?.therapies && (
          <DataTable
            items={data.therapyBreakdown}
            columns={[
              { key: "name", label: "Therapy" },
              { key: "count", label: "Sessions" },
            ]}
          />
        )}

        {/* Trend analysis */}
        {type === "trend_analysis" && data && (
          <div className="mt-2 space-y-2">
            {Object.entries(data).map(([key, val]) => (
              <div key={key} className="flex items-center justify-between px-2.5 py-2 bg-surface-hover rounded-button">
                <div>
                  <p className="text-xs font-medium text-content-primary capitalize">{key}</p>
                  <p className="text-[10px] text-content-tertiary">Confidence: {val.confidence_score}%</p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold capitalize ${
                    val.trend === "growing" ? "text-success-600" : val.trend === "declining" ? "text-danger-500" : "text-content-secondary"
                  }`}>
                    {val.trend}
                  </p>
                  <p className={`text-[10px] ${val.change >= 0 ? "text-success-600" : "text-danger-500"}`}>
                    {val.change > 0 ? "+" : ""}{val.change}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Report sections */}
        {type === "report" && data?.sections && (
          <div className="mt-3 space-y-2">
            {data.sections.map((section, i) => (
              <ReportSection key={i} section={section} />
            ))}
          </div>
        )}

        {/* Report recommendations */}
        {type === "report" && data?.recommendations && (
          <div className="mt-3 space-y-1.5">
            <h4 className="text-xs font-semibold text-content-primary">Recommendations</h4>
            {data.recommendations.map((rec, i) => (
              <RecommendationCard key={i} rec={rec} />
            ))}
          </div>
        )}

        {/* Fallback suggestions */}
        {(type === "fallback" || type === "error") && (
          <SuggestionChips suggestions={suggestions} onSuggestionClick={onSuggestionClick} />
        )}

        {/* Help / greeting suggestions */}
        {(type === "help" || type === "greeting") && suggestions && (
          <SuggestionChips suggestions={suggestions} onSuggestionClick={onSuggestionClick} />
        )}
      </div>

      {/* Footer action */}
      {route && actionLabel && (
        <div className="px-3.5 py-2 border-t border-surface-border">
          <button
            onClick={handleNavigate}
            className="w-full text-center text-xs font-medium text-brand-500 hover:text-brand-600 py-1.5 rounded-button hover:bg-brand-500/5 transition-all"
          >
            {actionLabel} →
          </button>
        </div>
      )}
    </div>
  );
};

export default AssistantResponseRenderer;
